import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Course } from '../types';
import courseService from '../services/course.service';
import { toast } from 'react-toastify';

interface CourseChoiceContentProps {
  onSelect: (course: Course) => void;
}

const CourseChoiceContent: React.FC<CourseChoiceContentProps> = ({ onSelect }) => {
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAvailableCourses();
  }, []);

  const loadAvailableCourses = async () => {
    try {
      setLoading(true);
      const courses = await courseService.getAvailableCourses();
      setAvailableCourses(courses);
    } catch (error) {
      toast.error('Erreur lors du chargement des cours disponibles');
    } finally {
      setLoading(false);
    }
  };

  const handleAdoptCourse = async (course: Course) => {
    try {
      const adoptedCourse = await courseService.adoptCourse(course.id);
      toast.success(`Cours "${course.title}" ajouté à votre atelier`);
      onSelect(adoptedCourse);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du cours');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Sélectionnez un cours existant à ajouter à votre atelier
      </Typography>
      <List>
        {availableCourses.map((course) => (
          <ListItem key={course.id} disablePadding>
            <ListItemButton onClick={() => handleAdoptCourse(course)}>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">{course.title}</Typography>
                    {course.is_public && <Chip label="Public" size="small" color="primary" />}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    Créé par: {course.created_by_name || 'Utilisateur'}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
        {availableCourses.length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" py={4}>
            Aucun cours disponible à ajouter
          </Typography>
        )}
      </List>
    </>
  );
};

export default CourseChoiceContent;